import { QueryFactory } from '@lmt/shared-types';
import { WaitingListVisitItem } from '@lmt/unicorn-shared/app/components/calendar/waiting-list/WaitingListVisitItem';
import { isAnyObjectInDataSourceSuitFilter } from '@lmt/unicorn-shared/app/components/documents/components/data-source-helpers';
import { useSubtenantsDict } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import {
  DXDS,
  useDataSource,
} from '@lmt/unicorn-shared/app/common/devexpress/dx-data-source';
import React, { FC, useMemo } from 'react';

import styles from './WaitingList.module.scss';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';
import { defaultVisitColor } from '../devextreme/common';
import { getBGColor } from '../color-settings/components/color-parser';

interface Props {
  subtenants: number[];
  disableTitle?: boolean;
}

export const WaitingList: FC<Props> = React.memo(function (props) {
  const { subtenants: subtenantsInFilter } = props;
  useDataSource();
  const i18next = useScopedTranslation('calendar.waiting_list');
  const colorSettingsQuery =
    QueryFactory.AppointmentColorSettingsQuery.useGetQuery();
  const subtenants = useSubtenantsDict();
  const isNightTheme = useUniSelector(getSettings).isNightTheme;

  const dateNow = useMemo(() => new Date(), []);

  const { data: visits } =
    QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({
      from: dateNow,
      subtenants: subtenantsInFilter,
      isWaitingList: true,
    });

  const { data: resources } = QueryFactory.ResourceQuery.useGetResourcesQuery();

  const colorSettingsRules = useMemo(() => {
    return colorSettingsQuery.data?.rules ?? [];
  }, [colorSettingsQuery.data]);

  return (
    <div className={styles.container} data-test-id={'calendar-waiting-list'}>
      {props.disableTitle ? null : (
        <div className={styles.header}>
          <div className={styles.title}>{i18next.t('title')}</div>
        </div>
      )}
      <div className={styles.appointmentList}>
        {visits
          ?.sort((a, b) =>
            a.priority! > b.priority! ||
            (a.priority! === b.priority! && a.start > b.start)
              ? 1
              : -1,
          )
          .map((visit) => {
            const dataSource = new DXDS.DataSource([visit]);
            const fittingRule = colorSettingsRules.find((rule) => {
              const parsedFilterExpressions = (rule as any)
                .parsedFilterExpression as any;
              return isAnyObjectInDataSourceSuitFilter(
                dataSource,
                parsedFilterExpressions,
              );
            });
            const color = getBGColor(
              fittingRule?.color ?? defaultVisitColor,
              isNightTheme,
            );
            return (
              <WaitingListVisitItem
                key={visit.id}
                visit={visit}
                subtenant={subtenants[visit.subtenantId]}
                color={color}
                resources={resources?.data ?? []}
              />
            );
          })}
      </div>
    </div>
  );
});
